import { Text, InputSelect } from 'components';
import {
  DEBOUNCE_TEXT,
  MIN_NUMBER_SEARCH,
  PAGE_SIZE,
  TEXT_STRING,
  queryKeys,
  routes,
} from '../../constants';
import CardGirl from './CardGirl';
import { TRecommendFemale } from 'types';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getRecommendFemale } from 'api';

const STR = TEXT_STRING.CALL_WOMAN;

type Props = {
  listSelectedGirl: TRecommendFemale[];
  setListSelectedGirl: React.Dispatch<React.SetStateAction<TRecommendFemale[]>>;
  isCallNewGirl?: boolean;
  fixUserId?: number;
};

let timeout: NodeJS.Timeout;

const SearchFemale = ({
  listSelectedGirl,
  setListSelectedGirl,
  isCallNewGirl = false,
  fixUserId,
}: Props): JSX.Element => {
  // State
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');

  const {
    data: listRecommendFemale,
    fetchNextPage: fetchMore,
    hasNextPage: hasMore,
    isPending: loadingFemale,
  } = useInfiniteQuery({
    queryKey: [queryKeys.RECOMMEND_FEMALE, searchQuery],
    queryFn: async ({ pageParam }) =>
      getRecommendFemale({
        currentPage: pageParam,
        pageSize: PAGE_SIZE,
        search: searchQuery,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
    enabled: !!searchQuery,
  });

  // Memo & Callbacks
  const listRecommendFemaleView = useMemo(
    () => listRecommendFemale?.pages.map(({ data }) => data).flat() || [],
    [listRecommendFemale?.pages],
  );

  // Hook
  const navigate = useNavigate();

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(async () => {
        if (e.target.value.trim().length >= MIN_NUMBER_SEARCH) {
          setSearchQuery(e.target.value.trim());
        }
      }, DEBOUNCE_TEXT);
    },
    [setSearchQuery],
  );

  const handleChooseGirl = useCallback(
    (value: TRecommendFemale) => {
      setListSelectedGirl((prev) => {
        if (prev.some((girl) => girl.id === value.id)) {
          if (fixUserId === value.id) {
            return prev;
          }
          return prev.filter((item) => item.id !== value.id);
        } else {
          const newSelectedGirlList = [...prev, value];
          return newSelectedGirlList;
        }
      });
    },
    [setListSelectedGirl, fixUserId],
  );

  const onSelectValue = useCallback(
    (value: TRecommendFemale) => {
      handleChooseGirl(value);
      setSearchText('');
    },
    [handleChooseGirl],
  );

  return (
    <>
      <div className="w-full flex flex-col gap-8px">
        <Text bold fontSize={14}>
          {STR.INPUT_ID}
        </Text>
        <Text textColor="text-neutral" fontSize={14}>
          {STR.NOMINATE_HINT}
        </Text>
        <InputSelect
          fetchMore={fetchMore}
          hasMore={hasMore}
          searchValue={searchText}
          onSelectValue={onSelectValue}
          data={listRecommendFemaleView}
          selectedData={listSelectedGirl}
          onSearchValue={handleSearch}
          placeholder={TEXT_STRING.CALL_WOMAN.INPUT_PLACEHOLDER}
          loading={loadingFemale}
        />
        <div className="flex overflow-x-auto pb-16px pt-8px">
          <div className="flex gap-8px">
            {listSelectedGirl.map((female, index) => {
              return (
                <CardGirl
                  disabledChoose={fixUserId === female.id}
                  data={female}
                  key={index}
                  isChoose={listSelectedGirl.some(
                    (selectedGirl) =>
                      fixUserId !== selectedGirl.id &&
                      selectedGirl.id === female.id,
                  )}
                  onChoose={() => {
                    handleChooseGirl(female);
                  }}
                  onDetail={() => {
                    navigate(
                      `${routes.DETAIL_FEMALE.replace(':id', `${female.id}`)}`,
                    );
                  }}
                  icon="minus"
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchFemale;
